import axios from "axios";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
export const API = `${BACKEND_URL}/api`;

// JWT bearer token storage.
// NOTE: Google/OAuth flow uses an httpOnly `session_token` cookie set by the
// backend (more secure). This localStorage path is for the email/password
// JWT flow only - the token is a short-lived JWT (7 days) bound to user+role,
// and cannot be used to mint new credentials.
const TOKEN_KEY = "chefcheck.token";

// Super-admin impersonation tokens.
// When a super_admin clicks "Acessar painel" on a company, we keep their
// original auth token under IMP_ORIGIN_TOKEN_KEY and store the short-lived
// impersonation JWT under IMP_TOKEN_KEY. The axios interceptor sends the
// impersonation token (when present) instead of the regular token, which
// also overrides the Google session_token cookie on the backend.
const IMP_TOKEN_KEY = "chefcheck.imp_token";
const IMP_ORIGIN_TOKEN_KEY = "chefcheck.imp_origin_token";
const IMP_COMPANY_KEY = "chefcheck.imp_company";

export function getToken() {
  try {
    return localStorage.getItem(TOKEN_KEY);
  } catch (err) {
    console.warn("localStorage unavailable", err);
    return null;
  }
}

export function setToken(token) {
  try {
    if (token) {
      localStorage.setItem(TOKEN_KEY, token);
    } else {
      localStorage.removeItem(TOKEN_KEY);
    }
  } catch (err) {
    console.warn("localStorage unavailable", err);
  }
}

export function getImpersonationToken() {
  try {
    return localStorage.getItem(IMP_TOKEN_KEY);
  } catch (err) {
    return null;
  }
}

export function getImpersonationCompany() {
  try {
    const raw = localStorage.getItem(IMP_COMPANY_KEY);
    return raw ? JSON.parse(raw) : null;
  } catch (err) {
    return null;
  }
}

export function isImpersonating() {
  return !!getImpersonationToken();
}

export function startImpersonation({ token, company }) {
  try {
    // Preserve the super-admin's current token so we can restore it on exit.
    const original = localStorage.getItem(TOKEN_KEY);
    if (original) {
      localStorage.setItem(IMP_ORIGIN_TOKEN_KEY, original);
    } else {
      localStorage.removeItem(IMP_ORIGIN_TOKEN_KEY);
    }
    localStorage.setItem(IMP_TOKEN_KEY, token);
    localStorage.setItem(IMP_COMPANY_KEY, JSON.stringify(company || {}));
  } catch (err) {
    console.warn("localStorage unavailable", err);
  }
}

export function endImpersonation() {
  try {
    const original = localStorage.getItem(IMP_ORIGIN_TOKEN_KEY);
    localStorage.removeItem(IMP_TOKEN_KEY);
    localStorage.removeItem(IMP_COMPANY_KEY);
    localStorage.removeItem(IMP_ORIGIN_TOKEN_KEY);
    if (original) {
      localStorage.setItem(TOKEN_KEY, original);
    }
  } catch (err) {
    console.warn("localStorage unavailable", err);
  }
}

export const api = axios.create({
  baseURL: API,
  withCredentials: true,
});

api.interceptors.request.use((config) => {
  // Impersonation token wins over the regular token.
  const impToken = getImpersonationToken();
  const token = impToken || getToken();
  if (token) {
    config.headers.Authorization = `Bearer ${token}`;
  }
  return config;
});

api.interceptors.response.use(
  (response) => response,
  (error) => {
    if (error?.response?.status === 401) {
      const path = window.location.pathname;
      // If impersonation token expired, fall back to original super_admin token.
      if (isImpersonating()) {
        endImpersonation();
        window.location.assign("/admin/companies");
        return Promise.reject(error);
      }
      if (!path.startsWith("/login") && !path.startsWith("/register") && path !== "/" && !path.startsWith("/pricing")) {
        setToken(null);
      }
    }
    return Promise.reject(error);
  }
);
